import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, GripVertical, BarChart3 } from "lucide-react";

const inputCls = "w-full bg-white border border-black/10 rounded-sm px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] outline-none";
const MAX_STATS = 8;
const MIN_STATS = 1;

// Generate a stable client-side UID for each stat so React keys don't rely on array index.
let _statUid = 0;
const withUid = (s) => ({ ...s, _uid: `s${++_statUid}` });

export default function AdminSiteStats() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site/hero-stats")
      .then((r) => setStats((r.data?.stats || []).map(withUid)))
      .finally(() => setLoading(false));
  }, []);

  const setRow = (idx, key, v) => setStats((arr) => arr.map((s, i) => i === idx ? { ...s, [key]: v } : s));
  const add = () => setStats((arr) => (arr.length >= MAX_STATS ? arr : [...arr, withUid({ value: "", label: "" })]));
  const remove = (idx) => setStats((arr) => (arr.length <= MIN_STATS ? arr : arr.filter((_, i) => i !== idx)));
  const moveUp = (idx) => {
    if (idx === 0) return;
    setStats((arr) => { const n = [...arr]; [n[idx - 1], n[idx]] = [n[idx], n[idx - 1]]; return n; });
  };
  const moveDown = (idx) => {
    if (idx === stats.length - 1) return;
    setStats((arr) => { const n = [...arr]; [n[idx], n[idx + 1]] = [n[idx + 1], n[idx]]; return n; });
  };

  const save = async (e) => {
    e.preventDefault();
    for (const s of stats) {
      if (!s.value.trim() || !s.label.trim()) {
        toast.error("Each stat needs a value and label.");
        return;
      }
    }
    setSaving(true);
    try {
      await api.put("/site/hero-stats", { stats: stats.map((s) => ({ value: s.value.trim(), label: s.label.trim() })) });
      toast.success("Hero stats updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-site-stats" className="p-8 lg:p-12 max-w-3xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.25em] text-[#525252]">Manage</div>
        <h1 className="font-display text-4xl font-bold mt-1 inline-flex items-center gap-2">
          <BarChart3 size={28} className="text-[var(--brand-primary)]"/> Home hero stats
        </h1>
        <p className="mt-3 text-[#525252]">These appear in the marquee under the homepage hero (e.g. "240+ Restaurants boosted"). Add up to {MAX_STATS}.</p>
      </div>

      {loading ? (
        <div className="text-[#525252]" data-testid="hero-stats-loading">Loading…</div>
      ) : (
        <form onSubmit={save} className="bg-white border border-black/10 p-6 space-y-4" data-testid="hero-stats-form">
          {stats.map((s, idx) => (
            <div key={s._uid} className="grid grid-cols-[28px,140px,1fr,40px] gap-3 items-center" data-testid={`hero-stat-row-${idx}`}>
              <div className="flex flex-col items-center text-[#525252]">
                <button type="button" onClick={() => moveUp(idx)} disabled={idx === 0}
                        className="text-[10px] disabled:opacity-30" title="Move up">▲</button>
                <GripVertical size={12} className="opacity-30"/>
                <button type="button" onClick={() => moveDown(idx)} disabled={idx === stats.length - 1}
                        className="text-[10px] disabled:opacity-30" title="Move down">▼</button>
              </div>
              <input
                type="text" value={s.value} placeholder="e.g. 240+"
                data-testid={`hero-stat-value-${idx}`} maxLength={20}
                onChange={(e) => setRow(idx, "value", e.target.value)}
                className={`${inputCls} font-display font-bold text-lg`}
              />
              <input
                type="text" value={s.label} placeholder="e.g. Restaurants boosted"
                data-testid={`hero-stat-label-${idx}`} maxLength={60}
                onChange={(e) => setRow(idx, "label", e.target.value)}
                className={inputCls}
              />
              <button type="button" onClick={() => remove(idx)} disabled={stats.length <= MIN_STATS}
                      data-testid={`hero-stat-remove-${idx}`}
                      className="p-2 hover:bg-rose-50 text-rose-700 rounded-sm disabled:opacity-30 disabled:hover:bg-transparent"
                      title={stats.length <= MIN_STATS ? "At least one stat is required" : "Remove"}>
                <Trash2 size={14}/>
              </button>
            </div>
          ))}

          <button
            type="button" onClick={add} disabled={stats.length >= MAX_STATS}
            data-testid="hero-stat-add"
            className="inline-flex items-center gap-2 text-sm border border-dashed border-black/20 px-4 py-2 rounded-sm hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] disabled:opacity-40 disabled:hover:border-black/20 disabled:hover:text-inherit"
          >
            <Plus size={14}/> Add stat {stats.length >= MAX_STATS && <span className="text-[10px]">(max {MAX_STATS})</span>}
          </button>

          <div className="pt-4 border-t border-black/10 flex items-center gap-3">
            <button
              type="submit" disabled={saving} data-testid="hero-stats-save"
              className="inline-flex items-center gap-2 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-5 py-2.5 rounded-sm font-medium disabled:opacity-60"
            >
              <Save size={14}/> {saving ? "Saving…" : "Save changes"}
            </button>
            <p className="text-xs text-[#525252]">Changes are live on the homepage immediately.</p>
          </div>
        </form>
      )}
    </div>
  );
}
